import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getAssetActiveUploadsCount, getAssetsUploadsTotalProgress, getAssetUploadsCount,
	getAssetUploadsProjectIDs, getETAForAllAssetUploads
} from 'src/selectors/assetUploads';
import { cancelAllAssetUploads, clearFinishedUploads } from 'src/actions/assetUploads';

import useReduxFileSelectorForAssetUpload
	from 'src/new_arch/hooks/useReduxFileSelectorForAssetUpload';
import { useRootStore } from 'src/new_arch/providers/RootStoreProvider';
import useReduxProject from 'src/new_arch/modules/projectView/hooks/useReduxProject';
import useFolderSync from '@uploads/hooks/useFolderSync';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';

export type UseAssetUploadsBoxProps = {
	projectID?: number,
	folderID?: number | null
};

const useAssetUploadsBox = ( {
	projectID, folderID = null
}: UseAssetUploadsBoxProps ) => {
	const uploadProjectIDs = useSelector( getAssetUploadsProjectIDs );
	const assetUploadsCount = useSelector( getAssetUploadsCount );
	const assetActiveUploadsCount = useSelector( getAssetActiveUploadsCount );
	const totalTimeRemaining = useSelector( getETAForAllAssetUploads );
	const totalProgress = useSelector( getAssetsUploadsTotalProgress );
	const { assetsFlatViewEnabled: isFlatViewEnabled } = useRootStore();
	const project = useReduxProject( projectID || 0 );

	const hasUploads = assetUploadsCount > 0;
	const isUploading = assetActiveUploadsCount > 0;
	const isInsideAproject = !!projectID;
	const currentUserCanUploadAssets = project?.userPermissions?.currentUserCan( 'upload', 'assets' ) || false;
	const canUploadItems = isInsideAproject && !isFlatViewEnabled && currentUserCanUploadAssets;

	const openFileSelectorForAssetUpload = useReduxFileSelectorForAssetUpload( {
		projectID: projectID || 0, folderID
	} )
	const onStartUploadClicked = useCallback(
		() => openFileSelectorForAssetUpload( { directoryMode: false } ),
		[ openFileSelectorForAssetUpload ]
	);
	const dispatch = useDispatch();
	const onCancelAllClick = useCallback( () => dispatch( cancelAllAssetUploads() ), [ dispatch ] );
	const onClearAllClick = useCallback( () => dispatch( clearFinishedUploads() ), [ dispatch ] );

	const {
		isBrowserSupported: isFolderSyncSupported,
		isWatching,
		folderName,
		syncedFiles,
		startWatching,
		stopWatching
	} = useFolderSync( { projectID: projectID || 0, folderID: folderID ?? null } );

	const { folderSyncStore } = useRootStore();
	const snackbarSystem = useSnackbarSystem();

	const startWatchingWithNotification = useCallback( async () => {
		const started = await startWatching();
		if ( started ) {
			snackbarSystem.showOutlinedInfoMessage( {
				information: 'Only files directly inside the selected folder will be synced. Subfolders are not included.'
			} );
		}
	}, [ startWatching, snackbarSystem ] );

	useEffect( () => {
		folderSyncStore.setStartWatching( startWatchingWithNotification );
		return () => folderSyncStore.unsetStartWatching();
	}, [ startWatchingWithNotification ] );

	return {
		uploadProjectIDs,
		assetUploadsCount,
		assetActiveUploadsCount,
		totalTimeRemaining,
		totalProgress,
		hasUploads,
		isUploading,
		canUploadItems,
		onStartUploadClicked,
		onCancelAllClick,
		onClearAllClick,
		isFolderSyncSupported,
		isWatching,
		folderName,
		syncedCount: syncedFiles.length,
		startWatching: startWatchingWithNotification,
		stopWatching
	};
};

export default useAssetUploadsBox;
